import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Submission, TableColumn } from '../../types/domain';
import { RBACPermissionTier, DataTableColumn, ColumnDataType, FilterRule } from '../../types/grid';
import { DataTable } from './DataTable';
import { IdCell } from './IdCell';
import { RowDetailsDrawer } from './RowDetailsDrawer';
import { EmptyHedgehog } from '../brand/EmptyHedgehog';
import { inferColumnType } from './buildGridColumns';
import { apiClient, downloadExportBlob } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { Modal } from '../ui/Modal';
import { ConfirmModal } from '../modals/ConfirmModal';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Textarea';
import { Plus, Trash2, Eye, ShieldAlert, CheckCircle2 } from 'lucide-react';

interface DataGridProps {
  projectId: string;
  tableId: string;
  tableSlug: string;
  ingestToken?: string;
  columns: TableColumn[];
  rows: Submission[];
  totalRows: number;
  unfilteredTotal?: number | null;
  page: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newSize: number) => void;
  onRowsUpdated?: () => void;
  userTier: RBACPermissionTier;
  isLoading?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
  onServerSearchChange?: (search: string, idFilter?: string) => void;
  filterRules?: FilterRule[];
  onFilterChange?: (rules: FilterRule[]) => void;
  sortCol?: string;
  sortDir?: 'asc' | 'desc';
  sortRules?: Array<{ id: string; desc: boolean }>;
  onSortChange?: (column: string, direction: 'asc' | 'desc', allSorts?: Array<{ id: string; desc: boolean }>) => void;
  onNavigateEndpoints?: () => void;
  persistenceKey?: string;
}

export const DataGrid: React.FC<DataGridProps> = ({
  projectId,
  tableId,
  tableSlug,
  ingestToken,
  columns: configuredColumns,
  rows: initialRows,
  totalRows,
  unfilteredTotal,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRowsUpdated,
  userTier,
  isLoading = false,
  onRefresh,
  isRefreshing,
  onEditingChange,
  onServerSearchChange,
  filterRules,
  onFilterChange,
  sortCol,
  sortDir,
  sortRules,
  onSortChange,
  onNavigateEndpoints,
  persistenceKey,
}) => {
  const { showToast } = useToast();

  // Drawer state
  const [inspectedRow, setInspectedRow] = useState<Submission | null>(null);

  // Local state for optimistic updates
  // Local state for optimistic updates
  const [localRows, setLocalRows] = useState<Submission[]>(initialRows);
  const prevInitialRowsRef = useRef(initialRows);
  if (prevInitialRowsRef.current !== initialRows) {
    prevInitialRowsRef.current = initialRows;
    setLocalRows(initialRows);
  }

  const localRowsRef = useRef(localRows);
  localRowsRef.current = localRows;

  // Multi-row selection state
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [batchDeleteModalOpen, setBatchDeleteModalOpen] = useState(false);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // Add Row Modal state
  const [addRowOpen, setAddRowOpen] = useState(false);
  const [newRowPayload, setNewRowPayload] = useState('{\n  "email": "user@example.com",\n  "name": "Jane Doe"\n}');
  const [isAddingRow, setIsAddingRow] = useState(false);

  const canEdit = userTier === 'edit' || userTier === 'full';

  // Optimistic cell commit with automatic rollback
  const handleCellCommit = useCallback(
    async (rowId: string, colId: string, newValue: any, oldValue: any) => {
      if (!canEdit || newValue === oldValue) return;
      const colKey = colId.replace(/^data_/, '');

      // 1. Snapshot previous state for rollback
      const prevRows = localRowsRef.current;

      // 2. Optimistic update
      setLocalRows((prev) =>
        prev.map((r) => {
          if (r.id === rowId) {
            return {
              ...r,
              data: {
                ...r.data,
                [colKey]: newValue,
              },
            };
          }
          return r;
        })
      );

      // 3. API mutation
      try {
        await apiClient(`/projects/${projectId}/tables/${tableId}/rows/${rowId}`, {
          method: 'PUT',
          body: JSON.stringify({
            [colKey]: newValue,
          }),
        });

        showToast({
          type: 'success',
          title: 'Cell Saved',
          message: `Field '${colKey}' updated successfully.`,
          duration: 2000,
        });
      } catch (err: any) {
        // Rollback on failure
        setLocalRows(prevRows);
        showToast({
          type: 'error',
          title: 'Save Failed — Reverted',
          message: err?.message || 'Unauthorized or network failure',
        });
      }
    },
    [canEdit, projectId, tableId, showToast]
  );

  // Delete row mutation
  const handleDeleteRow = useCallback(
    async (rowId: string) => {
      if (!canEdit) return;

      const prevRows = localRowsRef.current;
      setLocalRows((prev) => prev.filter((r) => r.id !== rowId));

      try {
        await apiClient(`/projects/${projectId}/tables/${tableId}/rows/${rowId}`, {
          method: 'DELETE',
        });

        showToast({
          type: 'success',
          title: 'Row Deleted',
          message: `Submission ${rowId} removed.`,
        });

        if (inspectedRow?.id === rowId) {
          setInspectedRow(null);
        }
        onRowsUpdated?.();
      } catch (err: any) {
        setLocalRows(prevRows);
        showToast({
          type: 'error',
          title: 'Delete Failed',
          message: err?.message || 'Server error',
        });
      }
    },
    [canEdit, projectId, tableId, inspectedRow, onRowsUpdated, showToast]
  );

  const handleDeleteRowRef = useRef(handleDeleteRow);
  handleDeleteRowRef.current = handleDeleteRow;

  // Batch delete selected rows mutation
  const handleBatchDelete = useCallback(async () => {
    if (!canEdit || selectedRowIds.length === 0) return;
    setIsBatchDeleting(true);

    const idsToDelete = [...selectedRowIds];
    const prevRows = localRowsRef.current;

    // Optimistically remove from local state
    setLocalRows((prev) => prev.filter((r) => !idsToDelete.includes(r.id)));
    setSelectedRowIds([]);
    setBatchDeleteModalOpen(false);

    try {
      await apiClient(`/projects/${projectId}/tables/${tableId}/rows/batch-delete`, {
        method: 'POST',
        body: JSON.stringify({ ids: idsToDelete }),
      });

      showToast({
        type: 'success',
        title: 'Rows Deleted',
        message: `Successfully deleted ${idsToDelete.length} submission${idsToDelete.length > 1 ? 's' : ''}.`,
      });

      onRowsUpdated?.();
    } catch (err: any) {
      try {
        await Promise.all(
          idsToDelete.map((id) =>
            apiClient(`/projects/${projectId}/tables/${tableId}/rows/${id}`, {
              method: 'DELETE',
            })
          )
        );

        showToast({
          type: 'success',
          title: 'Rows Deleted',
          message: `Successfully deleted ${idsToDelete.length} submission${idsToDelete.length > 1 ? 's' : ''}.`,
        });

        onRowsUpdated?.();
      } catch (fallbackErr: any) {
        setLocalRows(prevRows);
        showToast({
          type: 'error',
          title: 'Batch Delete Failed',
          message: fallbackErr?.message || err?.message || 'Server error',
        });
      }
    } finally {
      setIsBatchDeleting(false);
    }
  }, [canEdit, selectedRowIds, localRows, projectId, tableId, onRowsUpdated, showToast]);

  // Export all filtered rows as CSV or JSON
  const handleExportAll = useCallback(
    async (format: 'csv' | 'json') => {
      try {
        const url = `/projects/${projectId}/tables/${tableId}/export?format=${format}`;
        const defaultFilename = `${tableSlug || 'export'}.${format}`;
        await downloadExportBlob(url, defaultFilename);
        showToast({
          type: 'success',
          title: 'Export Download Complete',
          message: `Saved ${defaultFilename}`,
        });
      } catch (err: any) {
        showToast({
          type: 'error',
          title: 'Export Download Failed',
          message: err?.message || 'Server error',
        });
      }
    },
    [projectId, tableId, tableSlug, showToast]
  );

  // Export selected rows as CSV or JSON
  const handleExportSelected = useCallback(
    async (selectedRows: Submission[], format: 'csv' | 'json') => {
      const ids = selectedRows.map((r) => r.id);
      if (ids.length === 0) return;

      try {
        const url = `/projects/${projectId}/tables/${tableId}/export?format=${format}&ids=${ids.join(',')}`;
        const defaultFilename = `${tableSlug || 'export'}-selected-${ids.length}.${format}`;
        await downloadExportBlob(url, defaultFilename);
        showToast({
          type: 'success',
          title: 'Export Download Complete',
          message: `Saved ${defaultFilename}`,
        });
      } catch (err: any) {
        showToast({
          type: 'error',
          title: 'Export Download Failed',
          message: err?.message || 'Server error downloading selected rows',
        });
      }
    },
    [projectId, tableId, tableSlug, showToast]
  );

  // Create row mutation
  const handleAddRowSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;

    let parsedData: any = {};
    try {
      parsedData = JSON.parse(newRowPayload);
    } catch {
      showToast({
        type: 'error',
        title: 'Invalid JSON',
        message: 'Please enter valid JSON for the row payload.',
      });
      return;
    }

    setIsAddingRow(true);
    try {
      await apiClient(`/projects/${projectId}/tables/${tableId}/rows`, {
        method: 'POST',
        body: JSON.stringify(parsedData),
      });

      showToast({
        type: 'success',
        title: 'Row Created',
        message: 'New submission row added to datastore.',
      });

      setAddRowOpen(false);
      setNewRowPayload('{\n  "email": "user@example.com",\n  "name": "Jane Doe"\n}');
      onRowsUpdated?.();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to create row',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsAddingRow(false);
    }
  };

  // Update row from drawer
  const handleUpdateRowFromDrawer = async (rowId: string, updatedData: Record<string, any>) => {
    try {
      await apiClient(`/projects/${projectId}/tables/${tableId}/rows/${rowId}`, {
        method: 'PUT',
        body: JSON.stringify(updatedData),
      });

      setLocalRows((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, data: updatedData } : r))
      );

      if (inspectedRow && inspectedRow.id === rowId) {
        setInspectedRow({ ...inspectedRow, data: updatedData });
      }

      showToast({
        type: 'success',
        title: 'Row Updated',
        message: 'All fields saved successfully.',
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: err?.message || 'Server error',
      });
      throw err;
    }
  };

  // Compute dynamic JSON keys signature
  const dynamicKeysSignature = useMemo(() => {
    const configuredKeys = new Set(configuredColumns.map((c) => c.key));
    const dynamicKeys = new Set<string>();
    for (const row of localRows) {
      if (!row.data) continue;
      for (const key of Object.keys(row.data)) {
        if (!configuredKeys.has(key) && !key.startsWith('_')) {
          dynamicKeys.add(key);
        }
      }
    }
    return Array.from(dynamicKeys).sort().join(',');
  }, [configuredColumns, localRows]);

  const dynamicColumns = useMemo<DataTableColumn<Submission>[]>(() => {
    if (!dynamicKeysSignature) return [];
    const keys = dynamicKeysSignature.split(',');
    return keys.map((key) => {
      const sampleValues = localRowsRef.current.map((s: Submission) => s.data?.[key]);
      const inferredType = inferColumnType(sampleValues);

      const col: DataTableColumn<Submission> = {
        id: `data_${key}`,
        header: () => (
          <span className="flex items-center gap-1.5" title="Discovered from JSONB payload">
            <span>{key}</span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 font-medium">
              AUTO
            </span>
          </span>
        ),
        accessorFn: (row) => row.data?.[key],
        dataType: inferredType,
        width: 180,
        minWidth: 120,
        maxWidth: 280,
        sortable: true,
        filterable: true,
        editable: canEdit,
      };
      return col;
    });
  }, [dynamicKeysSignature, canEdit]);

  // Build DataTable declarative columns
  const dataTableColumns = useMemo<DataTableColumn<Submission>[]>(() => {
    const list: DataTableColumn<Submission>[] = [
      {
        id: 'id',
        header: 'Submission ID',
        accessorKey: 'id',
        width: 140,
        minWidth: 130,
        maxWidth: 170,
        pinned: 'left',
        isSystem: true,
        sortable: true,
        filterable: true,
        cell: ({ row }) => <IdCell id={row.id} />,
      },
    ];

    // Configured Columns from database
    const sortedConfigured = [...configuredColumns].sort((a, b) => a.position - b.position);

    for (const col of sortedConfigured) {
      list.push({
        id: `data_${col.key}`,
        header: col.name || col.key,
        accessorFn: (row) => row.data?.[col.key],
        dataType: (col.data_type || 'text') as ColumnDataType,
        width: Math.min(Math.max(col.width || 180, 120), 280),
        minWidth: 120,
        maxWidth: 280,
        sortable: true,
        filterable: true,
        editable: canEdit,
        subfields: col.validation_rules?.subfields,
      });
    }

    // Add discovered dynamic columns
    list.push(...dynamicColumns);

    // Ingested At Timestamp
    list.push({
      id: 'created_at',
      header: 'Ingested At',
      accessorKey: 'created_at',
      dataType: 'date',
      width: 180,
      minWidth: 160,
      maxWidth: 200,
      sortable: true,
      filterable: true,
    });

    // Status: OK vs SPAM
    list.push({
      id: 'status',
      header: 'Status',
      accessorFn: (row) => (row.is_spam || row.status === 'quarantined' ? 'SPAM' : 'OK'),
      dataType: 'status',
      options: [
        { value: 'OK', label: 'OK', color: 'emerald' },
        { value: 'SPAM', label: 'SPAM', color: 'rose' },
      ],
      width: 90,
      minWidth: 85,
      maxWidth: 110,
      sortable: true,
      filterable: true,
      cell: ({ row }) => {
        const isSpam = row.is_spam || row.status === 'quarantined';
        return isSpam ? (
          <div className="flex items-center gap-1.5 whitespace-nowrap" title="Spam trapped by honeypot">
            <ShieldAlert size={13} className="text-rose-500 dark:text-rose-400 flex-shrink-0" />
            <span className="text-xs font-medium text-cf-textPrimary">SPAM</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 whitespace-nowrap" title="Clean submission">
            <CheckCircle2 size={13} className="text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
            <span className="text-xs font-medium text-cf-textPrimary">OK</span>
          </div>
        );
      },
    });

    // Actions Column: Inspect row details & Delete row
    list.push({
      id: 'actions',
      header: 'Actions',
      width: 100,
      minWidth: 96,
      maxWidth: 120,
      pinned: 'right',
      isSystem: true,
      sortable: false,
      filterable: false,
      hideable: false,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1 px-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={(e) => {
              e.stopPropagation();
              setInspectedRow(row);
            }}
            className="text-cf-textSecondary hover:text-cf-textPrimary"
            title="Inspect row details & JSON"
          >
            <Eye size={14} />
          </Button>

          {canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRowRef.current(row.id);
              }}
              className="text-cf-textSecondary hover:text-rose-500"
              title="Delete submission row"
            >
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      ),
    });

    return list;
  }, [configuredColumns, dynamicColumns, canEdit]);

  const mappedInitialSorting = useMemo(() => {
    if (!sortRules || sortRules.length === 0) return undefined;
    return sortRules.map((s) => ({
      id:
        s.id.startsWith('data_') ||
        s.id === 'created_at' ||
        s.id === 'status' ||
        s.id === 'id'
          ? s.id
          : `data_${s.id}`,
      desc: s.desc,
    }));
  }, [sortRules]);

  return (
    <div className="flex flex-col h-full w-full min-w-0 max-w-full">
      <DataTable<Submission>
        key={tableId}
        persistenceKey={persistenceKey || (tableId ? `table_${tableId}` : undefined)}
        data={localRows}
        columns={dataTableColumns}
        serverSide={true}
        totalRows={totalRows}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        onRefresh={onRefresh || onRowsUpdated}
        onServerSearchChange={(val) => onServerSearchChange?.(val)}
        onServerFilterChange={onFilterChange}
        initialFilterRules={filterRules}
        unfilteredTotalRows={unfilteredTotal ?? undefined}
        onServerSortChange={(field, dir, allSorts) => {
          if (onSortChange) {
            const rawCol = field.replace(/^data_/, '');
            const cleanedAllSorts = (allSorts || []).map((s) => ({
              id: s.id.replace(/^data_/, ''),
              desc: s.desc,
            }));
            if (rawCol && (dir === 'asc' || dir === 'desc')) {
              onSortChange(rawCol, dir, cleanedAllSorts);
            } else {
              onSortChange('', 'desc', cleanedAllSorts);
            }
          }
        }}
        initialSorting={mappedInitialSorting}
        initialSortField={
          sortCol
            ? sortCol === 'created_at' || sortCol === 'status' || sortCol === 'id' || sortCol.startsWith('data_')
              ? sortCol
              : `data_${sortCol}`
            : 'created_at'
        }
        initialSortDir={sortDir || 'desc'}
        enableSelection={true}
        selectedRowIds={selectedRowIds}
        onSelectionChange={setSelectedRowIds}
        canEdit={canEdit}
        onCellCommit={handleCellCommit}
        onEditingChange={onEditingChange}
        primaryAction={
          canEdit
            ? {
                label: 'New Row',
                icon: <Plus size={13} />,
                onClick: () => setAddRowOpen(true),
              }
            : undefined
        }
        bulkActions={[
          {
            label: 'Delete Selected',
            icon: <Trash2 size={13} />,
            variant: 'danger',
            onClick: () => setBatchDeleteModalOpen(true),
          },
        ]}
        onExportSelected={handleExportSelected}
        onExportAll={handleExportAll}
        tableName={tableSlug || 'submissions'}
        emptyState={
          totalRows === 0 && !isLoading ? (
            <EmptyHedgehog
              type="no-submissions"
              tableSlug={tableSlug}
              ingestToken={ingestToken || tableSlug}
              columns={configuredColumns}
              onAction={canEdit ? () => setAddRowOpen(true) : undefined}
              onNavigateEndpoints={onNavigateEndpoints}
            />
          ) : undefined
        }
      />

      {/* Pinned Slide-Out Row Details Drawer */}
      {inspectedRow && (
        <RowDetailsDrawer
          row={inspectedRow}
          columns={configuredColumns}
          onClose={() => setInspectedRow(null)}
          userTier={userTier}
          onUpdateRow={handleUpdateRowFromDrawer}
          onDelete={() => handleDeleteRow(inspectedRow.id)}
          onNavigate={(direction) => {
            const currentIndex = localRows.findIndex((r) => r.id === inspectedRow.id);
            const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
            if (nextIndex >= 0 && nextIndex < localRows.length) {
              setInspectedRow(localRows[nextIndex]);
            }
          }}
        />
      )}

      {/* Add New Row Modal */}
      <Modal
        isOpen={addRowOpen}
        onClose={() => setAddRowOpen(false)}
        title="Insert Submission Row"
      >
        <form onSubmit={handleAddRowSubmit} className="space-y-4">
          <Textarea
            label="JSON Data Payload"
            rows={8}
            mono
            value={newRowPayload}
            onChange={(e) => setNewRowPayload(e.target.value)}
          />

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-cf-border">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAddRowOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={isAddingRow}
              loading={isAddingRow}
              icon={!isAddingRow ? <Plus size={13} /> : undefined}
            >
              <span>{isAddingRow ? 'Inserting...' : 'Insert Row'}</span>
            </Button>
          </div>
        </form>
      </Modal>

      {/* Batch Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={batchDeleteModalOpen}
        onClose={() => setBatchDeleteModalOpen(false)}
        onConfirm={handleBatchDelete}
        title="Delete Selected Submissions"
        message={`Are you sure you want to permanently delete ${selectedRowIds.length} selected submission${selectedRowIds.length > 1 ? 's' : ''}? This action cannot be undone.`}
        confirmLabel={isBatchDeleting ? 'Deleting...' : `Delete ${selectedRowIds.length} Submissions`}
        isDestructive={true}
        isLoading={isBatchDeleting}
      />
    </div>
  );
};
